import React from 'react';
import ReactDOM from 'react-dom';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import { Provider } from 'react-redux';
import createHistory from 'history/createBrowserHistory';
import { Redirect, Route, Switch } from 'react-router';
import { ConnectedRouter, routerMiddleware, routerReducer } from 'react-router-redux';
import logger from 'redux-logger';
import registerServiceWorker from './registerServiceWorker';

import App            from './views/App';
import Widget         from './views/Widget';
import CodeUnlockPage from './views/components/CodeUnlockPage';
import CardsPage      from './views/components/CardsPage';

import { withAppShell } from './views/components/AppShell';

import * as reducers from './reducers';
import './index.css';
import './util/helpers/i18n';

import { verifyAuthentication } from './actions/AuthActions';

const history    = createHistory();
const middleware = routerMiddleware(history);
const store      = createStore(
    combineReducers({
        ...reducers,
        router: routerReducer,
    }),
    applyMiddleware(middleware, logger)
);

// Re-validate existing session on hard refresh
const token = localStorage.getItem('token');
if (token) {
    store.dispatch(verifyAuthentication({ token }, store.dispatch));
}

// ── Wrap every page with AppShell ─────────────────────────────────────────
// AppShell guarantees competition data is loaded before any child renders,
// applies the background image/color, and handles locale / i18n.
const ShellApp            = withAppShell(App);
const ShellWidget         = withAppShell(Widget);
const ShellCodeUnlockPage = withAppShell(CodeUnlockPage);
const ShellCardsPage      = withAppShell(CardsPage);

/**
 * AuthRedirect
 *
 * Catches any unmatched route. If the user is logged in, sends them to
 * /cards (the default post-login page). Otherwise falls through to the
 * root "/" which renders the main App (login/register view).
 *
 * Used as the last <Route> inside every <Switch> block.
 */
const AuthRedirect = ({ loggedIn, cardsPath }) =>
    loggedIn
        ? <Redirect to={cardsPath} />
        : <Redirect to="/" />;

const restrictedNames = ['/widget'];

if (
    (window && window.location && window.location.hash) ||
    window.location.pathname === '/' ||
    restrictedNames.includes(window.location.pathname)
) {
    // ── Root / hash-based routing ─────────────────────────────────────────
    ReactDOM.render(
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <Switch>
                    <Route exact path="/"          component={ShellApp} />
                    <Route path="/widget"          component={ShellWidget} />
                    <Route path="/leaderboard"     component={ShellApp} />
                    <Route exact path="/unlock"          component={ShellCodeUnlockPage} />
                    <Route exact path="/unlock/:code"    component={ShellCodeUnlockPage} />
                    <Route path="/cards"                 component={ShellCardsPage} />
                    {/* Catch-all: logged-in → /cards, logged-out → / */}
                    <Route render={() => {
                        const auth = store.getState().default.auth;
                        return auth.loggedIn
                            ? <Redirect to="/cards" />
                            : <Redirect to="/" />;
                    }} />
                </Switch>
            </ConnectedRouter>
        </Provider>,
        document.getElementById('root')
    );
} else {
    // ── Brand/act prefixed routing ────────────────────────────────────────
    ReactDOM.render(
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <Switch>
                    <Route exact path="/:brand/:act"             component={ShellApp} />
                    <Route exact path="/:brand/:act/widget"      component={ShellWidget} />
                    <Route exact path="/:brand/:act/leaderboard" component={ShellApp} />
                    <Route exact path="/:brand/:act/unlock"           component={ShellCodeUnlockPage} />
                    <Route exact path="/:brand/:act/unlock/:code"     component={ShellCodeUnlockPage} />
                    <Route exact path="/:brand/:act/cards"            component={ShellCardsPage} />
                    {/* Catch-all: logged-in → /:brand/:act/cards, logged-out → /:brand/:act */}
                    <Route path="/:brand/:act" render={({ match }) => {
                        const auth = store.getState().default.auth;
                        return auth.loggedIn
                            ? <Redirect to={`${match.url}/cards`} />
                            : <Redirect to={match.url} />;
                    }} />
                </Switch>
            </ConnectedRouter>
        </Provider>,
        document.getElementById('root')
    );
}

registerServiceWorker();
